
import { User } from '@/types/userRole';

// Define Manager and ManagerInvite interfaces here since they're missing or incompatible
export interface Manager {
  id: string;
  name: string;
  email: string;
  role: 'manager';
  status: 'active' | 'inactive';
  createdAt: Date;
  permissions: string[];
}

export interface ManagerInvite {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'canceled';
  expiresAt: Date;
  createdAt: Date;
  businessId: string; // Required field
}

// Mock data for managers
const mockManagers: Manager[] = [
  {
    id: 'manager-1',
    name: 'John Doe',
    email: 'john@example.com',
    role: 'manager',
    status: 'active',
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
    permissions: ['manage_users', 'view_reports']
  },
  {
    id: 'manager-2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    role: 'manager',
    status: 'active',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    permissions: ['manage_users', 'view_reports', 'edit_settings']
  },
  {
    id: 'manager-3',
    name: 'Bob Johnson',
    email: 'bob@example.com',
    role: 'manager',
    status: 'inactive',
    createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    permissions: ['view_reports']
  }
];

// Mock data for pending invites
const mockInvites: ManagerInvite[] = [
  {
    id: 'invite-1',
    name: 'Mike Wilson',
    email: 'mike@example.com',
    status: 'pending',
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    businessId: 'business-1'
  },
  {
    id: 'invite-2',
    name: 'Sarah Brown',
    email: 'sarah@example.com',
    status: 'pending',
    expiresAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    businessId: 'business-1'
  }
];

// Get all managers
export const getManagers = async (instanceId?: string): Promise<Manager[]> => {
  // In a real app, we would fetch from database and filter by instanceId
  await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
  return [...mockManagers];
};

// Get pending invites
export const getPendingInvites = async (instanceId?: string): Promise<ManagerInvite[]> => {
  // In a real app, we would fetch from database and filter by instanceId
  await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
  return [...mockInvites];
};

// Invite a new manager - adding export here
export const inviteManager = async (name: string, email: string): Promise<ManagerInvite> => {
  // In a real app, we would create in database and send email
  await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
  
  const newInvite: ManagerInvite = {
    id: `invite-${Date.now()}`,
    name,
    email,
    status: 'pending',
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    createdAt: new Date(),
    businessId: 'business-1' // Added required field
  };
  
  mockInvites.push(newInvite);
  return newInvite;
};

// Cancel invite - adding export here
export const cancelInvite = async (inviteId: string): Promise<boolean> => {
  // In a real app, we would update in database
  await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
  
  const inviteIndex = mockInvites.findIndex(invite => invite.id === inviteId);
  if (inviteIndex === -1) {
    throw new Error('Invite not found');
  }
  
  mockInvites.splice(inviteIndex, 1);
  return true;
};

// Deactivate manager
export const deactivateManager = async (managerId: string): Promise<boolean> => {
  // In a real app, we would update in database
  await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
  
  const managerIndex = mockManagers.findIndex(manager => manager.id === managerId);
  if (managerIndex === -1) {
    throw new Error('Manager not found');
  }
  
  mockManagers[managerIndex].status = 'inactive';
  return true;
};

// Reactivate manager
export const reactivateManager = async (managerId: string): Promise<boolean> => {
  // In a real app, we would update in database
  await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
  
  const managerIndex = mockManagers.findIndex(manager => manager.id === managerId);
  if (managerIndex === -1) {
    throw new Error('Manager not found');
  }
  
  mockManagers[managerIndex].status = 'active';
  return true;
};

// Convert manager to User type for compatibility
export const convertManagerToUser = (manager: Manager): User => {
  return {
    ...manager,
    id: manager.id,
    name: manager.name,
    email: manager.email,
    role: 'manager',
    createdAt: manager.createdAt,
    isMasterAdmin: false,
    isAdmin: true,
    isSuperAdmin: false
  };
};
